/**
 * Data access layer — all reads/writes go directly to Firestore
 * using the Firebase v9 modular SDK. No backend server required.
 *
 * Every function scopes its Firestore path to the currently
 * signed-in user: users/{uid}/{collection}/{docId}
 *
 * Client-side validation + sanitization lives here; the authoritative
 * guard is firestore.rules (defense in depth).
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { auth, db } from "../firebase";

// ── Auth / path helpers ────────────────────────────────────────────────────────

function uid() {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in");
  return u.uid;
}

/** Throws if the current user has not verified their email.
 *  Reads must stay open so the banner / onboarding still work; only
 *  mutations are gated. Firestore rules enforce the same check. */
function requireVerified() {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in");
  if (!u.emailVerified) {
    throw new Error("Please verify your email before saving changes.");
  }
}

function userCol(name) {
  return collection(db, "users", uid(), name);
}

function userDoc(colName, id) {
  return doc(db, "users", uid(), colName, id);
}

function toObj(snapshot) {
  return { id: snapshot.id, ...snapshot.data() };
}

// ── Validation / sanitization helpers ──────────────────────────────────────────

/** Trim + length cap for user-entered strings.
 *  Removes null bytes and control chars other than tab/newline. */
function cleanString(v, { maxLen = 200, required = false, field = "field" } = {}) {
  if (v === undefined || v === null) v = "";
  if (typeof v !== "string") v = String(v);
  // Strip control chars (except \t \n \r)
  v = v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "").trim();
  if (v.length > maxLen) v = v.slice(0, maxLen);
  if (required && v.length === 0) {
    throw new Error(`${field} is required.`);
  }
  return v;
}

function cleanNumber(v, { min = -1e12, max = 1e12, required = false, field = "field" } = {}) {
  if (v === "" || v === undefined || v === null) {
    if (required) throw new Error(`${field} is required.`);
    return null;
  }
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) throw new Error(`${field} must be a number.`);
  if (n < min || n > max) throw new Error(`${field} is out of range.`);
  return n;
}

function cleanInt(v, opts) {
  const n = cleanNumber(v, opts);
  return n === null ? null : Math.trunc(n);
}

function cleanEnum(v, allowed, { required = true, field = "field" } = {}) {
  if ((v === undefined || v === null || v === "") && !required) return null;
  if (!allowed.includes(v)) {
    throw new Error(`${field} must be one of: ${allowed.join(", ")}`);
  }
  return v;
}

/** ISO-8601 date string (YYYY-MM-DD) or null. Keeps it as stored. */
function cleanDateString(v) {
  if (!v) return null;
  if (typeof v !== "string") return null;
  // Accept YYYY-MM-DD or full ISO; cap length defensively.
  const s = v.slice(0, 40);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s : null;
}

const PAYCHECK_FREQ   = ["weekly", "bi-weekly", "semi-monthly", "monthly"];
const BILL_FREQ       = ["weekly", "monthly", "annual", "one-time"];
const BILL_CATEGORIES = [
  "Housing", "Utilities", "Subscriptions", "Transportation",
  "Groceries", "Insurance", "Entertainment", "Medical", "Other",
];

// ── Paychecks ──────────────────────────────────────────────────────────────────

export async function getPaychecks() {
  const q = query(userCol("paychecks"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(toObj);
}

export async function createPaycheck(data) {
  requireVerified();
  const payload = {
    label:           cleanString(data.label,      { maxLen: 100, required: true, field: "Label" }),
    spouseName:      cleanString(data.spouseName, { maxLen: 100 }),
    amount:          cleanNumber(data.amount,     { min: 0, max: 1e7, required: true, field: "Amount" }),
    frequency:       cleanEnum(data.frequency, PAYCHECK_FREQ, { field: "Frequency" }),
    startDate:       cleanDateString(data.startDate),
    semiMonthlyDay1: cleanInt(data.semiMonthlyDay1, { min: 1, max: 31 }) ?? 1,
    semiMonthlyDay2: cleanInt(data.semiMonthlyDay2, { min: 1, max: 31 }) ?? 15,
    createdAt:       serverTimestamp(),
  };
  const ref = await addDoc(userCol("paychecks"), payload);
  return { id: ref.id, ...payload };
}

export async function updatePaycheck(id, data) {
  requireVerified();
  const changes = { updatedAt: serverTimestamp() };
  if (data.label      !== undefined) changes.label      = cleanString(data.label,      { maxLen: 100, required: true, field: "Label" });
  if (data.spouseName !== undefined) changes.spouseName = cleanString(data.spouseName, { maxLen: 100 });
  if (data.amount     !== undefined) changes.amount     = cleanNumber(data.amount,     { min: 0, max: 1e7, required: true, field: "Amount" });
  if (data.frequency  !== undefined) changes.frequency  = cleanEnum(data.frequency, PAYCHECK_FREQ, { field: "Frequency" });
  if (data.startDate  !== undefined) changes.startDate  = cleanDateString(data.startDate);
  if (data.semiMonthlyDay1 !== undefined) changes.semiMonthlyDay1 = cleanInt(data.semiMonthlyDay1, { min: 1, max: 31 }) ?? 1;
  if (data.semiMonthlyDay2 !== undefined) changes.semiMonthlyDay2 = cleanInt(data.semiMonthlyDay2, { min: 1, max: 31 }) ?? 15;
  await updateDoc(userDoc("paychecks", id), changes);
  return { id, ...changes };
}

export async function deletePaycheck(id) {
  requireVerified();
  await deleteDoc(userDoc("paychecks", id));
  return { success: true };
}

// ── Bills ──────────────────────────────────────────────────────────────────────

export async function getBills() {
  const q = query(userCol("bills"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(toObj);
}

export async function createBill(data) {
  requireVerified();
  const payload = {
    name:         cleanString(data.name,  { maxLen: 100, required: true, field: "Name" }),
    amount:       cleanNumber(data.amount,{ min: 0, max: 1e7, required: true, field: "Amount" }),
    dueDate:      cleanDateString(data.dueDate),
    frequency:    cleanEnum(data.frequency, BILL_FREQ, { field: "Frequency" }),
    category:     cleanEnum(data.category, BILL_CATEGORIES, { field: "Category" }),
    isPaid:       false,
    lastPaidDate: null,
    createdAt:    serverTimestamp(),
  };
  const ref = await addDoc(userCol("bills"), payload);
  return { id: ref.id, ...payload };
}

export async function updateBill(id, data) {
  requireVerified();
  const changes = { updatedAt: serverTimestamp() };
  if (data.name      !== undefined) changes.name      = cleanString(data.name, { maxLen: 100, required: true, field: "Name" });
  if (data.amount    !== undefined) changes.amount    = cleanNumber(data.amount, { min: 0, max: 1e7, required: true, field: "Amount" });
  if (data.dueDate   !== undefined) changes.dueDate   = cleanDateString(data.dueDate);
  if (data.frequency !== undefined) changes.frequency = cleanEnum(data.frequency, BILL_FREQ, { field: "Frequency" });
  if (data.category  !== undefined) changes.category  = cleanEnum(data.category, BILL_CATEGORIES, { field: "Category" });
  if (data.isPaid    !== undefined) changes.isPaid    = !!data.isPaid;
  await updateDoc(userDoc("bills", id), changes);
  return { id, ...changes };
}

export async function toggleBillPaid(id) {
  requireVerified();
  const ref  = userDoc("bills", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Bill not found");

  const current = snap.data().isPaid || false;
  const changes = {
    isPaid:       !current,
    lastPaidDate: !current ? new Date().toISOString() : null,
    updatedAt:    serverTimestamp(),
  };
  await updateDoc(ref, changes);
  return { id, ...changes };
}

export async function deleteBill(id) {
  requireVerified();
  await deleteDoc(userDoc("bills", id));
  return { success: true };
}

// ── Savings Goals ──────────────────────────────────────────────────────────────

export async function getSavings() {
  const q = query(userCol("savingsGoals"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(toObj);
}

export async function createSavingsGoal(data) {
  requireVerified();
  const payload = {
    name:          cleanString(data.name, { maxLen: 100, required: true, field: "Name" }),
    targetAmount:  cleanNumber(data.targetAmount, { min: 0, max: 1e9, required: true, field: "Target amount" }),
    currentAmount: cleanNumber(data.currentAmount, { min: 0, max: 1e9 }) ?? 0,
    targetDate:    cleanDateString(data.targetDate),
    contributions: [],
    createdAt:     serverTimestamp(),
  };
  const ref = await addDoc(userCol("savingsGoals"), payload);
  return { id: ref.id, ...payload };
}

export async function updateSavingsGoal(id, data) {
  requireVerified();
  const changes = { updatedAt: serverTimestamp() };
  if (data.name          !== undefined) changes.name          = cleanString(data.name, { maxLen: 100, required: true, field: "Name" });
  if (data.targetAmount  !== undefined) changes.targetAmount  = cleanNumber(data.targetAmount, { min: 0, max: 1e9, required: true, field: "Target amount" });
  if (data.currentAmount !== undefined) changes.currentAmount = cleanNumber(data.currentAmount, { min: 0, max: 1e9 }) ?? 0;
  if (data.targetDate    !== undefined) changes.targetDate    = cleanDateString(data.targetDate);
  await updateDoc(userDoc("savingsGoals", id), changes);
  return { id, ...changes };
}

export async function contributeToGoal(id, { amount, note }) {
  requireVerified();
  const amt = cleanNumber(amount, { min: 0.01, max: 1e9, required: true, field: "Contribution" });
  const contribution = {
    amount: amt,
    note:   cleanString(note, { maxLen: 200 }),
    date:   new Date().toISOString(),
  };
  await updateDoc(userDoc("savingsGoals", id), {
    currentAmount: increment(amt),
    contributions: arrayUnion(contribution),
    updatedAt:     serverTimestamp(),
  });
  return { success: true, contribution };
}

export async function deleteSavingsGoal(id) {
  requireVerified();
  await deleteDoc(userDoc("savingsGoals", id));
  return { success: true };
}

// ── Debts ──────────────────────────────────────────────────────────────────────

export async function getDebts() {
  const q = query(userCol("debts"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(toObj);
}

export async function createDebt(data) {
  requireVerified();
  const balance = cleanNumber(data.balance, { min: 0, max: 1e9, required: true, field: "Balance" });
  const payload = {
    name:            cleanString(data.name, { maxLen: 100, required: true, field: "Name" }),
    balance,
    originalBalance: balance,
    minimumPayment:  cleanNumber(data.minimumPayment, { min: 0, max: 1e7, required: true, field: "Minimum payment" }),
    apr:             cleanNumber(data.apr, { min: 0, max: 100 }) ?? 0,
    dueDate:         cleanDateString(data.dueDate),
    createdAt:       serverTimestamp(),
  };
  const ref = await addDoc(userCol("debts"), payload);
  return { id: ref.id, ...payload };
}

export async function updateDebt(id, data) {
  requireVerified();
  const changes = { updatedAt: serverTimestamp() };
  if (data.name           !== undefined) changes.name           = cleanString(data.name, { maxLen: 100, required: true, field: "Name" });
  if (data.balance        !== undefined) changes.balance        = cleanNumber(data.balance, { min: 0, max: 1e9, required: true, field: "Balance" });
  if (data.minimumPayment !== undefined) changes.minimumPayment = cleanNumber(data.minimumPayment, { min: 0, max: 1e7, required: true, field: "Minimum payment" });
  if (data.apr            !== undefined) changes.apr            = cleanNumber(data.apr, { min: 0, max: 100 }) ?? 0;
  if (data.dueDate        !== undefined) changes.dueDate        = cleanDateString(data.dueDate);
  await updateDoc(userDoc("debts", id), changes);
  return { id, ...changes };
}

export async function deleteDebt(id) {
  requireVerified();
  await deleteDoc(userDoc("debts", id));
  return { success: true };
}

// ── User settings ──────────────────────────────────────────────────────────────

export async function getUserSettings() {
  const ref = doc(db, "users", uid(), "settings", "preferences");
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : { idleTimeoutMinutes: 0 };
}

export async function updateUserSettings(data) {
  requireVerified();
  const payload = {
    idleTimeoutMinutes: cleanInt(data.idleTimeoutMinutes, { min: 0, max: 1440 }) ?? 0,
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, "users", uid(), "settings", "preferences"), payload, { merge: true });
  return payload;
}
